<script lang="ts">
    import * as Chart from "../../lib/components/ui/chart/index.js";
    import { getClient } from "../../lib/utils.js";
    import { locale } from "../../lib/locale.svelte.js";
    import { useURL } from "../../lib/url.svelte.js";
    import { BarChart, AreaChart, Bar, chartDataArray, Group, LinearGradient } from "layerchart";
    import {
        BigNumber,
        formatDate,
        parseISO,
        todayLocal,
        CalendarDate,
        singleOrDie,
        mapValues,
        sumBy,
        keyOrDie,
        indexOrDie,
        definedOrDie,
        range,
        z,
        CalendarDateSchema,
        startOfWeek,
        endOfWeek,
        singleToArrayCodec,
    } from "@repo/utils";
    import * as Card from "../../lib/components/ui/card/index.js";
    import Number from "../../lib/components/common/number.svelte";
    import * as Select from "../../lib/components/ui/select/index.js";
    import { m } from "../../lib/messages.svelte.js";
    import * as Popover from "../../lib/components/ui/popover";
    import { Button } from "../../lib/components/ui/button/index.js";
    import CalendarIcon from "@lucide/svelte/icons/calendar";
    import { RangeCalendar } from "../../lib/components/ui/range-calendar/index.js";
    import DateComponent from "../../lib/components/common/date.svelte";
    import type { DateRange } from "bits-ui";
    import * as Tabs from "../../lib/components/ui/tabs/index.js";
    import DashboardCases from "./transactions-table.svelte";
    import { InboxIdSchema } from "@repo/backend/shared";

    const url = useURL({
        by: z.enum(["day", "week", "month"]),
        tab: z.enum(["overview", "cases"]),
        from: CalendarDateSchema,
        to: CalendarDateSchema,
        inboxId: singleToArrayCodec(InboxIdSchema),
        partner: z.string(),
    });
    const by = $derived(url.query.by ?? "week");
    const from = $derived(url.query.from ?? startOfWeek(todayLocal().subtract({ months: 3 }), locale.current));
    const to = $derived(url.query.to ?? endOfWeek(todayLocal(), locale.current));
    const tab = $derived(url.query.tab ?? "overview");
    const period = $derived({ start: from as CalendarDate, end: to as CalendarDate });
    let tempRange = $state(period as DateRange);
    let calendarOpen = $state(false);

    $effect(() => {
        if (tempRange.start && tempRange.end) {
            url.query.from = tempRange.start as CalendarDate;
            url.query.to = tempRange.end as CalendarDate;
            calendarOpen = false;
        }
    });

    async function fetchData(_by: typeof by, _period: typeof period) {
        const response = await getClient()["Get dashboard data"]({
            query: {
                by: _by === "day" ? "date_id" : _by === "week" ? "week_start" : "month_start",
                from: _period.start.toString(),
                to: _period.end.toString(),
            },
        });
        return { by: _by, response };
    }
    let promise = $derived(fetchData(by, period));
    $effect(() => {
        const id = setInterval(() => {
            promise = fetchData(by, period);
        }, 120_000);
        return () => {
            clearInterval(id);
        };
    });
    async function fetchTotals(period_: typeof period) {
        return getClient()["Get dashboard data"]({
            query: {
                from: period_.start.toString(),
                to: period_.end.toString(),
            },
        });
    }
    let promiseTotals = $derived(fetchTotals(period));
    $effect(() => {
        const id = setInterval(() => {
            promiseTotals = fetchTotals(period);
        }, 120_000);
        return () => {
            clearInterval(id);
        };
    });

    const dateFormatter = $derived(
        new Intl.DateTimeFormat(locale.current, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }),
    );
    const monthFormatter = $derived(new Intl.DateTimeFormat(locale.current, { month: "short" }));
    const formatters: Record<string, (x: string) => string> = {
        day: (x) => dateFormatter.format(parseISO(x)),
        week: (x) => formatDate(x, "RRRR-II"),
        month: (x) => monthFormatter.format(parseISO(x)),
    };
    const colors = {
        casePendingCount: "var(--chart-1)",
        caseCancelledCount: "var(--chart-9)",
        caseProcessedWithChangesCount: "var(--chart-2)",
        caseProcessedWithoutChangesCount: "var(--chart-3)",
        orderChangeRequestCount: "var(--chart-5)",
        decisionAcceptedCustomerRatio: "var(--chart-1)",
        decisionAcceptedProductRatio: "var(--chart-9)",
        decisionRemovedProductRatio: "var(--chart-3)",
        decisionChangedProductRatio: "var(--chart-4)",
        decisionChangedCustomerRatio: "var(--chart-5)",
        decisionChangedQuantityRatio: "var(--chart-6)",
        decisionAddedProductRatio: "var(--chart-1)",
        decisionUnrecognizedProductRatio: "var(--chart-8)",
        decisionAcceptedSingleProductRatio: "var(--chart-1)",
        decisionAcceptedMultipleProductRatio: "var(--chart-4)",
        decisionNotAcceptedSingleProductRatio: "var(--chart-5)",
        decisionNotAcceptedMultipleProductRatio: "var(--chart-6)",
        decisionProductCount: "var(--chart-1)",
        decisionProcessedProductCount: "var(--chart-3)",
    };
    const chartConfig: Chart.ChartConfig = mapValues(colors, (k, v) => ({
        label: m[`dashboard.${k}`](),
        color: v,
    }));
</script>

<Tabs.Root bind:value={() => tab, (x) => (url.query.tab = x)} class="flex h-full flex-col overflow-hidden *:px-4">
    <div class="flex items-end gap-4 border-b p-2">
        <Tabs.List class="h-10">
            <Tabs.Trigger value="overview" class="px-6">
                {m["dashboard.tabs.overview"]()}
            </Tabs.Trigger>
            <Tabs.Trigger value="cases" class="px-6">
                {m["dashboard.tabs.cases"]()}
            </Tabs.Trigger>
        </Tabs.List>
        <div class="flex-1"></div>
        {#if tab === "overview"}
            <Select.Root type="single" bind:value={() => by, (x) => (url.query.by = x)}>
                <Select.Trigger id="by" class="w-35">{m[`dashboard.${by}`]()}</Select.Trigger>
                <Select.Content>
                    <Select.Item value="day">{m["dashboard.day"]()}</Select.Item>
                    <Select.Item value="week">{m["dashboard.week"]()}</Select.Item>
                    <Select.Item value="month">{m["dashboard.month"]()}</Select.Item>
                </Select.Content>
            </Select.Root>
        {/if}
        <Popover.Root
            onOpenChangeComplete={() => {
                tempRange = period;
            }}
            bind:open={calendarOpen}
        >
            <Popover.Trigger id="range">
                {#snippet child({ props })}
                    <Button {...props} variant="outline" class="flex w-60">
                        <CalendarIcon />
                        <DateComponent value={period.start} /> - <DateComponent value={period.end} />
                    </Button>
                {/snippet}
            </Popover.Trigger>
            <Popover.Content class="w-auto overflow-hidden p-0" align="end">
                <RangeCalendar
                    class="w-full"
                    fixedWeeks
                    weekStartsOn={1}
                    bind:value={tempRange}
                    locale={locale.current}
                />
            </Popover.Content>
        </Popover.Root>
    </div>

    {#if tab === "overview"}
        <div class="flex flex-col gap-4 overflow-auto py-4.25">
            {#await promiseTotals then data}
                {@const totals = singleOrDie(data)}
                {@const cards = [
                    { type: "number", key: "caseCount" },
                    { type: "number", key: "caseProcessedCount" },
                    { type: "percent", key: "caseProcessedRatio" },
                    {
                        type: "percent",
                        key: "decisionAcceptedCustomerRatio",
                    },
                    {
                        type: "percent",
                        key: "decisionAcceptedProductRatio",
                    },
                ] as const}
                <div class="flex gap-4">
                    {#each cards as x (x.key)}
                        <Card.Root class="grow basis-0 justify-between text-center">
                            <Card.Header class="flex grow items-center">
                                <Card.Title class="grow">{m[`dashboard.${x.key}`]()}</Card.Title>
                            </Card.Header>
                            <Card.Content>
                                <Number
                                    value={BigNumber(totals[x.key] ?? 0)}
                                    options={x.type === "percent"
                                        ? { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 }
                                        : undefined}
                                />
                            </Card.Content>
                        </Card.Root>
                    {/each}
                </div>
            {/await}
            {#await promise then data}
                {#key data.by}
                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>{m["dashboard.cases_"]()}</Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={chartConfig} class="aspect-auto h-75">
                                <BarChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    seriesLayout="stack"
                                    series={[
                                        "casePendingCount",
                                        "caseCancelledCount",
                                        "caseProcessedWithChangesCount",
                                        "caseProcessedWithoutChangesCount",
                                        "orderChangeRequestCount",
                                    ].map((key) => ({
                                        key,
                                        color: `var(--color-${key})`,
                                        label: chartConfig[key]?.label,
                                    }))}
                                    props={{
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet marks({ series, context })}
                                        {@const data = chartDataArray(context.data)}
                                        <Group class="lc-bars">
                                            {#each data as d, i (i)}
                                                {@const sum = sumBy(series, (s) => keyOrDie(d, s.key))}
                                                <LinearGradient
                                                    vertical
                                                    stops={sum === 0
                                                        ? []
                                                        : range(series.length - 1, 0)
                                                              .reduce<[number, string][]>((acc, si, j) => {
                                                                  const s = indexOrDie(series, si);
                                                                  const previous = acc[acc.length - 1];
                                                                  const from = previous?.[0] ?? 0;
                                                                  const to =
                                                                      j === series.length - 1
                                                                          ? 100
                                                                          : from + (keyOrDie(d, s.key) / sum) * 100;
                                                                  acc.push(
                                                                      [from, definedOrDie(s.color)],
                                                                      [to, definedOrDie(s.color)],
                                                                  );
                                                                  return acc;
                                                              }, [])
                                                              .map((x) => [`${x[0]}%`, x[1]] as [string, string])}
                                                >
                                                    {#snippet children({ gradient })}
                                                        <Bar
                                                            class="lc-bars-bar"
                                                            radius={4}
                                                            rounded="edge"
                                                            y={() => sum}
                                                            data={d}
                                                            strokeWidth={1}
                                                            fill={gradient}
                                                        />
                                                    {/snippet}
                                                </LinearGradient>
                                            {/each}
                                        </Group>
                                    {/snippet}
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]} />
                                    {/snippet}
                                </BarChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>

                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>{m["dashboard.casesCorrections"]()}</Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={chartConfig} class="aspect-auto h-75">
                                <BarChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    yDomain={[0, 1]}
                                    seriesLayout="group"
                                    series={[
                                        "decisionRemovedProductRatio",
                                        "decisionChangedProductRatio",
                                        "decisionChangedCustomerRatio",
                                        "decisionChangedQuantityRatio",
                                        "decisionUnrecognizedProductRatio",
                                        "decisionAddedProductRatio",
                                    ].map((key) => ({
                                        key,
                                        color: `var(--color-${key})`,
                                        label: chartConfig[key]?.label,
                                    }))}
                                    props={{
                                        yAxis: {
                                            format: (d) => `${d * 100}%`,
                                        },
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]}>
                                            {#snippet valueFormatter({ value })}
                                                <Number
                                                    value={BigNumber(value as number)}
                                                    options={{
                                                        style: "percent",
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 1,
                                                    }}
                                                />
                                            {/snippet}
                                        </Chart.Tooltip>
                                    {/snippet}
                                </BarChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>

                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>{m["dashboard.customerDetection"]()}</Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={chartConfig} class="aspect-auto h-75">
                                <AreaChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    yDomain={[0, 1]}
                                    series={["decisionAcceptedCustomerRatio"].map((key) => ({
                                        key,
                                        color: `var(--color-${key})`,
                                        label: chartConfig[key]?.label,
                                    }))}
                                    props={{
                                        yAxis: {
                                            format: (d) => `${d * 100}%`,
                                        },
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]}>
                                            {#snippet valueFormatter({ value })}
                                                <Number
                                                    value={BigNumber(value as number)}
                                                    options={{
                                                        style: "percent",
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 1,
                                                    }}
                                                />
                                            {/snippet}
                                        </Chart.Tooltip>
                                    {/snippet}
                                </AreaChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>

                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>{m["dashboard.productDetection"]()}</Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={chartConfig} class="aspect-auto h-75">
                                <AreaChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    yDomain={[0, 1]}
                                    series={["decisionAcceptedProductRatio"].map((key) => ({
                                        key,
                                        color: `var(--color-${key})`,
                                        label: chartConfig[key]?.label,
                                    }))}
                                    props={{
                                        yAxis: {
                                            format: (d) => `${d * 100}%`,
                                        },
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]}>
                                            {#snippet valueFormatter({ value })}
                                                <Number
                                                    value={BigNumber(value as number)}
                                                    options={{
                                                        style: "percent",
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 1,
                                                    }}
                                                />
                                            {/snippet}
                                        </Chart.Tooltip>
                                    {/snippet}
                                </AreaChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>

                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>{m["dashboard.productMatches"]()}</Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={chartConfig} class="aspect-auto h-75">
                                <BarChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    yDomain={[0, 1]}
                                    seriesLayout="group"
                                    series={[
                                        "decisionUnrecognizedProductRatio",
                                        "decisionAcceptedSingleProductRatio",
                                        "decisionAcceptedMultipleProductRatio",
                                        "decisionNotAcceptedSingleProductRatio",
                                        "decisionNotAcceptedMultipleProductRatio",
                                    ].map((key) => ({
                                        key,
                                        color: `var(--color-${key})`,
                                        label: chartConfig[key]?.label,
                                    }))}
                                    props={{
                                        yAxis: {
                                            format: (d) => `${d * 100}%`,
                                        },
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]}>
                                            {#snippet valueFormatter({ value })}
                                                <Number
                                                    value={BigNumber(value as number)}
                                                    options={{
                                                        style: "percent",
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 1,
                                                    }}
                                                />
                                            {/snippet}
                                        </Chart.Tooltip>
                                    {/snippet}
                                </BarChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>

                    <Card.Root>
                        <Card.Header class="text-center">
                            <Card.Title>{m["dashboard.positions"]()}</Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <Chart.Container config={chartConfig} class="aspect-auto h-75">
                                <BarChart
                                    data={data.response}
                                    x={data.by === "day" ? "dateId" : data.by === "week" ? "weekStart" : "monthStart"}
                                    seriesLayout="group"
                                    series={["decisionProductCount", "decisionProcessedProductCount"].map((key) => ({
                                        key,
                                        color: `var(--color-${key})`,
                                        label: chartConfig[key]?.label,
                                    }))}
                                    props={{
                                        xAxis: {
                                            format: formatters[data.by],
                                        },
                                    }}
                                    legend
                                >
                                    {#snippet tooltip()}
                                        <Chart.Tooltip labelFormatter={formatters[data.by]} />
                                    {/snippet}
                                </BarChart>
                            </Chart.Container>
                        </Card.Content>
                    </Card.Root>
                {/key}
            {/await}
        </div>
    {:else if tab === "cases"}
        <DashboardCases from={period.start.toString()} to={period.end.toString()} />
    {/if}
</Tabs.Root>
